/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useRef, useState } from 'react';
import Router from 'next/router';
import Auth from '@aws-amplify/auth';
import {
  Alert,
  AlertIcon,
  Button,
  FormLabel,
  HStack,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  useColorModeValue as mode,
  useColorModeValue,
  useToast
} from '@chakra-ui/react';

type VerifyCodeFormProps = {
  user: {
    username: string;
    password: string;
  };
  callback?: () => void;
};

function VerifyCodeForm({ user, callback }: VerifyCodeFormProps) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCodeUpdate = (value: string): void => {
    setCode(value);
  };

  const handleResendCode = async () => {
    setLoading(true);

    try {
      const resp = await Auth.resendSignUp(user.username);
      setLoading(false);
      toast({
        title: 'Resent email confirmation code.',
        description: `We've resent the email confirmation code to ${resp.CodeDeliveryDetails.Destination}`,
        status: 'info',
        variant: 'subtle',
        duration: 4000
      });
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Limit reached. Please wait a few minutes before trying again.');
    }
  };

  const handleCodeComplete = async (val: string) => {
    setError('');
    setLoading(true);

    // try to confirm verification code
    const codeConfirmed = await Auth.confirmSignUp(user.username, val).catch((err) => {
      console.error({ action: 'onConfirmCode:Auth.confirmSignUp:error', error: err });
      setCode('');
      setError('Invalid code. Please use the latest email confirmation code and try again.');
      setLoading(false);
      inputRef.current.focus();
    });

    // returned if not verified
    if (!codeConfirmed) {
      return;
    }

    // Redirect tp sign-in for resend email code scenarios.
    if (!user.password && callback) {
      callback();
      return;
    }

    // try to auto sign in
    const signedIn = await Auth.signIn(user.username, user.password).catch((e) => {
      console.error({ action: 'onEmailVerified:Auth.signIn:error', error: e });
      setError('Could not sign in.');
      setLoading(false);
    });

    // return if user could not sign in
    if (!signedIn) {
      return;
    }

    // Route to account on successful login
    await Router.push('/dashboard').catch((e) => {
      console.error({ action: 'onEmailVerified:route:error', error: e });
      setError('Could not load account page');
    });
  };

  return (
    <>
      {/* Errors */}
      {error.length > 0 && (
        <Alert status='error' mb='4' borderRadius='md'>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Code */}
      <FormLabel>Email verification code</FormLabel>
      <HStack mb={6}>
        <PinInput
          otp
          autoFocus
          value={code}
          type='number'
          variant='filled'
          colorScheme={mode('whiteAlpha', 'blackAlpha')}
          size='lg'
          focusBorderColor='brand.300'
          // isDisabled={loading}   // Breaks autofocus
          // isInvalid={!!error}    // Interferes with other errors e.g. Resend pin limit
          onComplete={handleCodeComplete}
          onChange={handleCodeUpdate}
        >
          <PinInputField ref={inputRef} />
          <PinInputField />
          <PinInputField />
          <PinInputField />
          <PinInputField />
          <PinInputField />
        </PinInput>
        <Spinner display={loading ? 'block' : 'none'} color='brand.300' />
      </HStack>
      <Text size='xs' color={useColorModeValue('gray.500', 'gray.500')}>
        Please use the code sent to your registered email address.
      </Text>
      <Text size='xs' color={useColorModeValue('gray.500', 'gray.500')}>
        <Button variant='link' onClick={handleResendCode}>
          Did not receive the email?
        </Button>
      </Text>
    </>
  );
}

export default VerifyCodeForm;
